import { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { Shield, Phone, Key, Globe, AlertCircle, CheckCircle } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { parsePhoneNumber, isValidPhoneNumber, getCountries, getCountryCallingCode, CountryCode } from 'libphonenumber-js';

// Popular countries to show first
const POPULAR_COUNTRIES: CountryCode[] = ['US', 'GB', 'CA', 'AU', 'IL', 'IN', 'DE', 'FR', 'ES', 'IT'];

export function Login() {
  const { 
    isAuthenticated, 
    isLoading, 
    otpSent, 
    phoneNumber, 
    sendOTP, 
    verifyOTP, 
    register,
    resetAuth 
  } = useAuth();
  
  const [countryCode, setCountryCode] = useState<CountryCode>('US');
  const [phoneInput, setPhoneInput] = useState('');
  const [otp, setOtp] = useState('');
  const [showRegister, setShowRegister] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [isPhoneValid, setIsPhoneValid] = useState(false);

  // Detect user's country on mount
  useEffect(() => {
    detectUserCountry();
    resetAuth();
  }, []);

  // Detect country from timezone or use IP geolocation
  const detectUserCountry = async () => {
    try {
      // Try to detect from timezone
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const countryFromTimezone = getCountryFromTimezone(timezone);
      
      if (countryFromTimezone) {
        setCountryCode(countryFromTimezone);
        return;
      }

      // Fallback: Try IP geolocation
      const response = await fetch('https://ipapi.co/json/');
      const data = await response.json();
      if (data.country_code) {
        setCountryCode(data.country_code as CountryCode);
      }
    } catch (error) {
      console.log('Could not detect country, using default (US)');
    }
  };

  // Simple timezone to country mapping
  const getCountryFromTimezone = (timezone: string): CountryCode | null => {
    const timezoneMap: Record<string, CountryCode> = {
      'America/New_York': 'US',
      'America/Los_Angeles': 'US',
      'America/Chicago': 'US',
      'America/Denver': 'US',
      'Europe/London': 'GB',
      'Europe/Paris': 'FR',
      'Europe/Berlin': 'DE',
      'Asia/Jerusalem': 'IL',
      'Asia/Kolkata': 'IN',
      'Australia/Sydney': 'AU',
      'America/Toronto': 'CA',
    };
    return timezoneMap[timezone] || null;
  };

  // Validate phone number as user types
  useEffect(() => {
    if (!phoneInput.trim()) {
      setPhoneError('');
      setIsPhoneValid(false);
      return;
    }

    try {
      const fullNumber = `+${getCountryCallingCode(countryCode)}${phoneInput}`;
      const isValid = isValidPhoneNumber(fullNumber, countryCode);
      
      if (isValid) {
        setPhoneError('');
        setIsPhoneValid(true);
      } else {
        setPhoneError('Invalid phone number format');
        setIsPhoneValid(false);
      }
    } catch (error) {
      setPhoneError('Invalid phone number');
      setIsPhoneValid(false);
    }
  }, [phoneInput, countryCode]);

  // Format phone number for display
  const getFormattedPhone = (): string => {
    if (!phoneInput.trim()) return '';
    try {
      const fullNumber = `+${getCountryCallingCode(countryCode)}${phoneInput}`;
      const parsed = parsePhoneNumber(fullNumber, countryCode);
      return parsed ? parsed.formatInternational() : fullNumber;
    } catch {
      return `+${getCountryCallingCode(countryCode)} ${phoneInput}`;
    }
  };

  // Redirect if already authenticated
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  const handleSendOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isPhoneValid && phoneInput.trim()) {
      // Use libphonenumber-js to properly format the number
      // This handles removing leading zeros and other local formatting
      const fullNumber = `+${getCountryCallingCode(countryCode)}${phoneInput}`;
      const parsed = parsePhoneNumber(fullNumber, countryCode);
      const formattedNumber = parsed ? parsed.number : fullNumber;
      await sendOTP({ phone: formattedNumber });
    }
  };

  const handleVerifyOTP = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (otp.trim() && otp.length === 6) {
      const result = await verifyOTP({ phone: phoneNumber, code: otp.trim() });
      if (result?.isNewUser) {
        setShowRegister(true);
      }
    }
  };

  // Auto-submit when 6 digits are entered
  const handleOtpChange = (value: string) => {
    const digitsOnly = value.replace(/\D/g, '');
    setOtp(digitsOnly);
    
    // Auto-submit when 6 digits are entered
    if (digitsOnly.length === 6) {
      // Small delay to show the complete input before submitting
      setTimeout(() => {
        handleVerifyOTP();
      }, 300);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (firstName.trim() && lastName.trim()) {
      const success = await register({
        tenant_id: '', // Will be created by the hook
        phone: phoneNumber,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
      });
      
      if (success) {
        // Registration successful, user will be redirected
      }
    }
  };

  // Get all countries for dropdown
  const allCountries = getCountries();
  const sortedCountries = [
    ...POPULAR_COUNTRIES.filter(c => allCountries.includes(c)),
    ...allCountries.filter(c => !POPULAR_COUNTRIES.includes(c))
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="flex justify-center">
            <Shield className="h-16 w-16 text-primary-600" />
          </div>
          <h2 className="mt-6 text-3xl font-bold text-gray-900">
            Welcome to Chat Safe
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Secure authentication via WhatsApp - no SMS needed
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-8">
          {!otpSent ? (
            // Phone number form
            <form onSubmit={handleSendOTP} className="space-y-6">
              <div>
                <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-2">
                  Country
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Globe className="h-5 w-5 text-gray-400" />
                  </div>
                  <select
                    id="country"
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value as CountryCode)}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  >
                    {sortedCountries.map((country, index) => {
                      const isPopular = POPULAR_COUNTRIES.includes(country);
                      return (
                        <option key={country} value={country}>
                          {isPopular && index < POPULAR_COUNTRIES.length ? '⭐ ' : ''}
                          {country} (+{getCountryCallingCode(country)})
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Phone className="h-5 w-5 text-gray-400" />
                  </div>
                  <div className="absolute inset-y-0 left-10 pl-3 flex items-center pointer-events-none border-r border-gray-300 pr-3">
                    <span className="text-gray-500 text-sm">+{getCountryCallingCode(countryCode)}</span>
                  </div>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    required
                    value={phoneInput}
                    onChange={(e) => setPhoneInput(e.target.value.replace(/\D/g, ''))}
                    className={`block w-full pl-24 pr-10 py-2 border rounded-md placeholder-gray-400 focus:outline-none focus:ring-primary-500 ${
                      phoneError && phoneInput.trim() 
                        ? 'border-red-300 focus:border-red-500' 
                        : isPhoneValid 
                        ? 'border-green-300 focus:border-green-500' 
                        : 'border-gray-300 focus:border-primary-500'
                    }`}
                    placeholder="123456789"
                  />
                  {phoneInput.trim() && (
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      {isPhoneValid ? (
                        <CheckCircle className="h-5 w-5 text-green-500" />
                      ) : (
                        <AlertCircle className="h-5 w-5 text-red-500" />
                      )}
                    </div>
                  )}
                </div>
                
                {phoneInput.trim() && (
                  <div className="mt-2">
                    {phoneError ? (
                      <p className="text-xs text-red-600 flex items-center">
                        <AlertCircle className="h-3 w-3 mr-1" />
                        {phoneError}
                      </p>
                    ) : isPhoneValid ? (
                      <p className="text-xs text-green-600 flex items-center">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Valid: {getFormattedPhone()}
                      </p>
                    ) : null}
                  </div>
                )}
                
                <p className="mt-2 text-xs text-gray-500">
                  We'll send you a verification code via WhatsApp
                </p>
              </div>

              <button
                type="submit"
                disabled={isLoading || !isPhoneValid}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <LoadingSpinner size="sm" className="text-white" />
                ) : (
                  'Send Verification Code'
                )}
              </button>
            </form>
          ) : showRegister ? (
            // Registration form
            <form onSubmit={handleRegister} className="space-y-6">
              <div className="text-center mb-6">
                <h3 className="text-lg font-medium text-gray-900">Complete Your Profile</h3>
                <p className="text-sm text-gray-600">Please provide your details to continue</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                    First Name
                  </label>
                  <input
                    id="firstName"
                    name="firstName"
                    type="text"
                    required
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    placeholder="First name"
                  />
                </div>

                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                    Last Name
                  </label>
                  <input
                    id="lastName"
                    name="lastName"
                    type="text"
                    required
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Last name"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading || !firstName.trim() || !lastName.trim()}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <LoadingSpinner size="sm" className="text-white" />
                ) : (
                  'Create Account'
                )}
              </button>
            </form>
          ) : (
            // OTP verification form
            <form onSubmit={handleVerifyOTP} className="space-y-6">
              <div className="text-center mb-6">
                <Key className="mx-auto h-12 w-12 text-primary-600" />
                <h3 className="mt-2 text-lg font-medium text-gray-900">Enter Verification Code</h3>
                <p className="text-sm text-gray-600">
                  We sent a 6-digit code to {phoneNumber} via WhatsApp
                </p>
                <p className="text-xs text-gray-500 mt-2">
                  Check your WhatsApp messages for the verification code
                </p>
              </div>

              <div>
                <label htmlFor="otp" className="block text-sm font-medium text-gray-700">
                  Verification Code
                </label>
                <input
                  id="otp"
                  name="otp"
                  type="text"
                  inputMode="numeric"
                  required
                  maxLength={6}
                  value={otp}
                  onChange={(e) => handleOtpChange(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-center text-lg tracking-widest placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="000000"
                  autoFocus
                />
              </div>

              <button
                type="submit"
                disabled={isLoading || otp.length !== 6}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <LoadingSpinner size="sm" className="text-white" />
                ) : (
                  'Verify Code'
                )}
              </button>

              <button
                type="button"
                onClick={resetAuth}
                className="w-full text-sm text-primary-600 hover:text-primary-500"
              >
                Use a different phone number
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
